// ===============================
// AXOIX Header Component
// Clean navigation - no sample pages
// ===============================

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../images/logo/AXOIX_LOGO.svg";
import MobileMenu from "../MobileMenu/MobileMenu";
import LanguageCurrencySwitcher from "../LanguageCurrencySwitcher";
import MegaMenu1 from "./MegaMenu1";

// Click-to-call button in the top bar. The number label is hidden below 1200px so
// the nav doesn't wrap; the icon (and the tel: link) stay on every breakpoint.
const headerStyles = `
  .header-call-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-right: 14px;
    padding: 9px 16px;
    border-radius: 100px;
    border: 1px solid rgba(0,255,151,0.35);
    background: rgba(0,255,151,0.08);
    color: var(--color-primary);
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    text-decoration: none;
    transition: all 0.25s ease;
  }
  .header-call-btn:hover {
    background: var(--color-primary);
    color: var(--color-secondary, #00020f);
    border-color: var(--color-primary);
  }
  .header-call-btn i { font-size: 14px; }
  @media (max-width: 1199px) {
    .header-call-btn__num { display: none; }
    .header-call-btn { padding: 9px 12px; margin-right: 10px; }
  }
`;

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isSticky, setIsSticky] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [mobileActive, setMobileActive] = useState(false);
  const lastScrollY = useRef<number>(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (currentScroll < lastScrollY.current && currentScroll > 100) {
        setIsSticky(true);
        setTimeout(() => setIsVisible(true), 10);
      } else if (currentScroll > lastScrollY.current) {
        setIsVisible(false);
        setTimeout(() => setIsSticky(false), 100);
      }

      if (currentScroll <= 100) {
        setIsSticky(false);
        setIsVisible(false);
      }

      lastScrollY.current = currentScroll;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const handleSubmit = (e: React.FormEvent) => e.preventDefault();

  return (
    <header
      id="xb-header-area"
      className="header-area header-style--one header-transparent is-sticky"
    >
      <style>{headerStyles}</style>
      {/* Jai Bhole Nath Top Bar */}
      <div className="jbn-top-bar">
        <img
          src={process.env.PUBLIC_URL + "/images/jaibholenath.png"}
          alt="Jai Bhole Nath"
          className="jbn-top-bar-img"
        />
      </div>

      <div
        className={`xb-header xb-sticky-stt ${
          isSticky ? "xb-header-area-sticky" : ""
        } ${isVisible ? "xb-header-fixed" : "xb-header-hidden"}`}
      >
        <div className="container mxw-1650">
          <div className="header__wrap ul_li_between">
            {/* Logo */}
            <div className="xb-header-logo">
              <Link to="/" className="logo1" onClick={handleClick}>
                <img src={logo} alt="AXOIX" style={{ height: "50px", width: "auto" }} />
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="main-menu__wrap navbar navbar-expand-lg p-0">
              <nav className="main-menu collapse navbar-collapse">
                <ul>
                  <li className="active">
                    <Link to="/" onClick={handleClick}>
                      <span>{t('nav.home')}</span>
                    </Link>
                  </li>

                  <li className="menu-item-has-children">
                    <Link to="/features" onClick={handleClick}>
                      <span>{t('nav.features')}</span>
                    </Link>
                    <ul className="submenu">
                      <li>
                        <Link to="/features" onClick={handleClick}>
                          <span>{t('ui.allModules')}</span>
                        </Link>
                      </li>
                      <li>
                        <Link to="/team" onClick={handleClick}>
                          <span>{t('ui.aiCharacters')}</span>
                        </Link>
                      </li>
                      <li>
                        <Link to="/how-it-connects" onClick={handleClick}>
                          <span>{t('ui.howItConnects', { defaultValue: 'How It Connects' })}</span>
                        </Link>
                      </li>
                    </ul>
                  </li>

                  <li className="menu-item-has-children megamenu">
                    <Link to="/industries" onClick={handleClick}>
                      <span>Industries</span>
                    </Link>
                    <MegaMenu1 onItemClick={handleClick} />
                  </li>

                  <li>
                    <Link to="/pricing" onClick={handleClick}>
                      <span>{t('nav.pricing')}</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/about" onClick={handleClick}>
                      <span>{t('nav.about')}</span>
                    </Link>
                  </li>

                  <li>
                    <Link to="/contact" onClick={handleClick}>
                      <span>{t('nav.contact')}</span>
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Language & Currency Switcher */}
            <LanguageCurrencySwitcher />

            {/* Click-to-call. tel: deep-links straight into the dialler on mobile
                and into the desktop's default calling app. */}
            <a
              href="tel:+918929222212"
              className="header-call-btn"
              aria-label="Call AXOIX on +91 89292 22212"
              title="Call +91 89292 22212"
            >
              <i className="fas fa-phone-alt" aria-hidden="true"></i>
              <span className="header-call-btn__num">+91 89292 22212</span>
            </a>

            {/* Button */}
            <div className="header-btn">
              <Link to="/signup" className="thm-btn" onClick={handleClick}>
                {t('nav.startTrial')}
              </Link>
            </div>

            {/* Mobile Toggle */}
            <div className="header-bar-mobile side-menu d-lg-none">
              {/* Icon-only control — needs an explicit name for screen readers. */}
              <button
                className="xb-nav-mobile"
                onClick={() => setMobileActive(!mobileActive)}
                aria-label={mobileActive ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={mobileActive}
              >
                <i className="far fa-bars" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className="xb-header-wrap">
            <div className={`xb-header-menu ${mobileActive ? "active" : ""}`}>
              <div className="xb-header-menu-scroll">
                <div
                  className="xb-menu-close xb-hide-xl xb-close"
                  onClick={() => setMobileActive(false)}
                ></div>

                <div className="xb-logo-mobile xb-hide-xl">
                  <Link to="/" rel="home">
                    <img src={logo} alt="AXOIX" style={{ height: "40px", width: "auto" }} />
                  </Link>
                </div>

                <div className="xb-header-mobile-search xb-hide-xl">
                  <form role="search" onSubmit={handleSubmit}>
                    <input
                      type="text"
                      placeholder="Search..."
                      name="s"
                      className="search-field"
                    />
                    <button className="search-submit" type="submit" aria-label="Search">
                      <i className="far fa-search" aria-hidden="true"></i>
                    </button>
                  </form>
                </div>

                <nav className="xb-header-nav">
                  <MobileMenu />
                </nav>
              </div>
            </div>
            <div className="xb-header-menu-backdrop"></div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
